import { configureStore, combineReducers, createAction } from '@reduxjs/toolkit'
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist'
import { useDispatch, useSelector } from 'react-redux'
import { api } from './api/base'
import userReducer from './slices/userSlice'
import accountReducer from './slices/accountSlice'
import sessionsReducer from './slices/sessionsSlice'
import sessionExpirationReducer from './slices/sessionExpirationSlice'
import { safeStorage } from './safeStorage'
import { sentryRtkMiddleware } from './middleware/sentryRtkMiddleware'
import { sessionExpiredMiddleware } from './middleware/sessionExpiredMiddleware'

const persistConfig = {
  key: 'pg19',
  storage: safeStorage,
  whitelist: ['user', 'account'],
}

const appReducer = combineReducers({
  user: userReducer,
  account: accountReducer,
  sessions: sessionsReducer,
  sessionExpiration: sessionExpirationReducer,
  [api.reducerPath]: api.reducer,
})

export const resetApp = createAction('app/reset')

const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: Parameters<typeof appReducer>[1],
) => {
  if (resetApp.match(action)) {
    return appReducer(undefined, action)
  }

  return appReducer(state, action)
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(api.middleware, sentryRtkMiddleware, sessionExpiredMiddleware),
})

export const persistor = persistStore(store)

export type RootState = ReturnType<typeof rootReducer>
export type AppDispatch = typeof store.dispatch
export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()
